import { useEffect, useMemo, useState } from "react"
import { Copy, Send, Loader2, Link2, Trash2, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { useToast } from "@/hooks/use-toast"
import { api } from "@/services/api"
import type { SecureInvite, SecureShareConfig } from "@/services/api"
import { useSecureShareStore } from "@/store/secureShareStore"

const TTL_PRESETS = [
  { value: 24, label: "1 Tag" },
  { value: 168, label: "7 Tage" },
  { value: 720, label: "30 Tage" },
]

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export function SecureInvitePanel() {
  const { toast } = useToast()
  const [config, setConfig] = useState<SecureShareConfig | null>(null)
  const [invites, setInvites] = useState<SecureInvite[]>([])
  const [loading, setLoading] = useState(true)

  const [label, setLabel] = useState("")
  const [recipient, setRecipient] = useState(
    () => useSecureShareStore.getState().prefill?.email || "",
  )
  const [ttlHours, setTtlHours] = useState(168)
  const [maxUses, setMaxUses] = useState("")
  const [allowFiles, setAllowFiles] = useState(true)

  const [creating, setCreating] = useState(false)
  const [createdUrl, setCreatedUrl] = useState<string | null>(null)
  const [createdId, setCreatedId] = useState<string | null>(null)

  async function refresh() {
    try {
      const list = await api.listSecureInvites()
      setInvites(list)
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    Promise.all([api.getSecureShareConfig().catch(() => null), api.listSecureInvites().catch(() => [])])
      .then(([cfg, list]) => {
        if (cfg) {
          setConfig(cfg)
          setTtlHours(cfg.inviteDefaultTtlHours)
          setAllowFiles(cfg.inviteAllowFiles)
        }
        setInvites(list as SecureInvite[])
      })
      .finally(() => setLoading(false))
  }, [])

  const ttlOptions = useMemo(() => {
    const max = config?.inviteMaxTtlHours ?? 720
    return TTL_PRESETS.filter((o) => o.value <= max)
  }, [config])

  async function create() {
    if (recipient && !EMAIL_RE.test(recipient.trim())) {
      toast({ title: "Ungültige E-Mail", variant: "destructive" })
      return
    }
    setCreating(true)
    try {
      const res = await api.createSecureInvite({
        label: label.trim() || undefined,
        recipientEmail: recipient.trim() || undefined,
        ttlHours,
        maxUses: maxUses ? Math.max(1, Number(maxUses)) : undefined,
        allowFiles,
      })
      setCreatedUrl(res.url)
      setCreatedId(res.id)
      toast({ title: "Einladung erstellt" })
      refresh()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast({ title: "Fehlgeschlagen", description: detail || "Nicht möglich.", variant: "destructive" })
    } finally {
      setCreating(false)
    }
  }

  async function sendMail() {
    if (!createdId || !createdUrl || !EMAIL_RE.test(recipient.trim())) return
    try {
      await api.sendSecureInviteMail(createdId, { to: recipient.trim(), url: createdUrl })
      toast({ title: "Einladung gesendet", description: `Link per E-Mail an ${recipient.trim()}.` })
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast({ title: "Versand fehlgeschlagen", description: detail || undefined, variant: "destructive" })
    }
  }

  async function revoke(id: string) {
    try {
      await api.revokeSecureInvite(id)
      refresh()
    } catch {
      toast({ title: "Widerruf fehlgeschlagen", variant: "destructive" })
    }
  }

  function resetForm() {
    setCreatedUrl(null)
    setCreatedId(null)
    setLabel("")
    setRecipient("")
    setMaxUses("")
  }

  function statusOf(inv: SecureInvite): { text: string; cls: string } {
    if (inv.revoked) return { text: "widerrufen", cls: "bg-muted text-muted-foreground" }
    if (!inv.active) return { text: "abgelaufen", cls: "bg-muted text-muted-foreground" }
    return { text: "aktiv", cls: "bg-primary/15 text-primary" }
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Erzeuge einen Einladungslink, über den eine externe Person das Tool wie ein
        Mitarbeiter nutzen kann (Text{allowFiles ? " + Dateien" : ""}, ohne öffentliche Limits).
      </p>

      {createdUrl ? (
        <div className="space-y-3 rounded-lg border border-primary/40 bg-primary/5 p-4">
          <p className="text-sm font-medium">Einladungslink bereit</p>
          <div className="flex gap-2">
            <Input readOnly value={createdUrl} className="font-mono text-xs" />
            <Button
              type="button"
              variant="secondary"
              onClick={() => { navigator.clipboard.writeText(createdUrl); toast({ title: "Kopiert" }) }}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex justify-end gap-2">
            {recipient && EMAIL_RE.test(recipient.trim()) && (
              <Button variant="outline" size="sm" onClick={sendMail}>
                <Send className="mr-1 h-4 w-4" /> Per E-Mail senden
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={resetForm}>Weitere Einladung</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3 rounded-xl border bg-muted/40 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Bezeichnung (optional)</Label>
              <Input className="mt-1 bg-background" placeholder="z. B. Firma / Name" value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Empfänger-E-Mail (optional)</Label>
              <Input className="mt-1 bg-background" type="email" placeholder="fuer den Mailversand" value={recipient} onChange={(e) => setRecipient(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Gültigkeit der Einladung</Label>
              <Select value={String(ttlHours)} onValueChange={(v) => setTtlHours(Number(v))}>
                <SelectTrigger className="mt-1 bg-background"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ttlOptions.map((o) => (
                    <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Max. Nutzungen (leer = unbegrenzt)</Label>
              <Input className="mt-1 bg-background" type="number" min={1} placeholder="unbegrenzt" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Dateien erlauben</Label>
            <Switch checked={allowFiles} onCheckedChange={setAllowFiles} disabled={!config?.inviteAllowFiles} />
          </div>
          <div className="flex justify-end">
            <Button onClick={create} disabled={creating}>
              {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
              Einladung erstellen
            </Button>
          </div>
        </div>
      )}

      <div>
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Link2 className="h-3.5 w-3.5" /> Meine Einladungen
        </div>
        {loading ? (
          <div className="flex h-16 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : invites.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Einladungen.</p>
        ) : (
          <div className="max-h-52 overflow-auto rounded-md border">
            {invites.map((inv) => {
              const st = statusOf(inv)
              return (
                <div key={inv.id} className="flex items-center justify-between gap-2 border-b px-3 py-2 text-sm last:border-b-0">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{inv.label || inv.recipientEmail || "Einladung"}</div>
                    <div className="text-xs text-muted-foreground">
                      {inv.useCount}{inv.maxUses ? `/${inv.maxUses}` : ""} genutzt · bis {new Date(inv.expiresAt).toLocaleDateString("de-DE")}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", st.cls)}>{st.text}</span>
                    {!inv.revoked && (
                      <button type="button" title="Widerrufen" onClick={() => revoke(inv.id)} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
